/**
 * Self-Talk (MYIHTP) — guião gerado por IA segundo o Dr. Shad Helmstetter
 * + PNL, focado nas áreas que a pessoa escolhe trabalhar, mais uma
 * biblioteca privada de áudios mp3 que a pessoa carrega para ouvir e
 * reprogramar-se. Design alinhado com o Coach Virtual (navy/verde/taupe).
 */
import { useEffect, useRef, useState } from "react";
import {
  MessageSquareQuote, Sparkles, Loader2, Trash2, Music, UploadCloud,
  CheckCircle2, ListChecks, Quote,
} from "lucide-react";
import { myApi } from "../../api";

const NAVY = "#0F1829";
const GREEN = "#4A5E2E";

const AREAS = [
  "Saúde", "Família", "Amor", "Carreira",
  "Finanças", "Desenvolvimento Pessoal", "Lazer", "Espiritualidade",
];

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export default function SelfTalk({ onError }) {
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [guiao, setGuiao] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    myApi.selfTalkHistory().then(setHistory).catch(() => {});
  }, []);

  const toggle = (a) =>
    setSelected((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));

  const gerar = async () => {
    if (!selected.length) { onError?.("Escolhe pelo menos uma área."); return; }
    setGenerating(true);
    try {
      const r = await myApi.selfTalkGenerate(selected);
      setGuiao(r.conteudo);
      const h = await myApi.selfTalkHistory();
      setHistory(h);
    } catch (e) { onError?.(e.message); }
    finally { setGenerating(false); }
  };

  const abrir = (h) => setGuiao(h.conteudo);

  const apagar = async (id) => {
    try {
      await myApi.selfTalkDelete(id);
      setHistory((hs) => hs.filter((h) => h.id !== id));
    } catch (e) { onError?.(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="selftalk-page">
      {/* Seletor de áreas + gerar */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="selftalk-generator">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquareQuote size={20} style={{ color: GREEN }} />
          <h2 className="text-lg font-semibold" style={{ color: NAVY }}>Gerar o teu Self-Talk</h2>
        </div>
        <p className="text-sm text-[#6B7280] mb-4">
          Afirmações de auto-programação positiva baseadas em PNL.
          Escolhe as áreas em que queres trabalhar.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {AREAS.map((a) => {
            const on = selected.includes(a);
            return (
              <button
                key={a}
                onClick={() => toggle(a)}
                data-testid={`selftalk-area-${a}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  on
                    ? "text-white border-transparent"
                    : "bg-white text-[#0F1829] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
                style={on ? { backgroundColor: GREEN } : undefined}
              >
                {on && <CheckCircle2 size={14} className="inline mr-1 -mt-0.5" />}
                {a}
              </button>
            );
          })}
        </div>

        <button
          onClick={gerar}
          disabled={generating || !selected.length}
          data-testid="selftalk-generate-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold disabled:opacity-50"
          style={{ backgroundColor: NAVY }}
        >
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {generating ? "A gerar…" : "Gerar Self-Talk"}
        </button>
      </div>

      {/* Resultado */}
      {guiao && (
        <div className="bg-[#FBFAF7] rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="selftalk-result">
          <h3 className="text-xl font-serif tracking-tight mb-2" style={{ color: NAVY }}>{guiao.titulo}</h3>
          {guiao.introducao && <p className="text-sm text-[#374151] mb-5">{guiao.introducao}</p>}

          <div className="space-y-5">
            {(guiao.secoes || []).map((s, i) => (
              <div key={i} data-testid={`selftalk-secao-${i}`}>
                <p className="text-sm font-semibold mb-2" style={{ color: GREEN }}>{s.area}</p>
                <ul className="space-y-1.5">
                  {(s.afirmacoes || []).map((af, j) => (
                    <li key={j} className="flex gap-2 text-[#0F1829] text-sm">
                      <Quote size={14} className="mt-1 shrink-0 text-[#978D78]" />
                      <span>{af}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {Array.isArray(guiao.comoUsar) && guiao.comoUsar.length > 0 && (
            <div className="mt-6 rounded-xl bg-white border border-[#E5E7EB] p-4">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks size={16} style={{ color: GREEN }} />
                <p className="text-sm font-semibold" style={{ color: NAVY }}>Como usar</p>
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm text-[#374151]">
                {guiao.comoUsar.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          {guiao.fraseFinal && (
            <p className="mt-5 text-center text-sm italic text-[#6B7280]">{guiao.fraseFinal}</p>
          )}
        </div>
      )}

      {/* Histórico de guiões */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="selftalk-history">
          <h3 className="text-sm font-semibold mb-3" style={{ color: NAVY }}>Os teus guiões guardados</h3>
          <ul className="divide-y divide-[#F3F4F6]">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between py-2.5 gap-3">
                <button onClick={() => abrir(h)} className="text-left flex-1 min-w-0" data-testid={`selftalk-hist-${h.id}`}>
                  <p className="text-sm font-medium text-[#0F1829] truncate">{h.titulo}</p>
                  <p className="text-xs text-[#9CA3AF]">{(h.areas || []).join(" · ")}</p>
                </button>
                <button onClick={() => apagar(h.id)} className="text-[#9CA3AF] hover:text-red-600" data-testid={`selftalk-hist-del-${h.id}`}>
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Biblioteca de áudios */}
      <AudioLibrary onError={onError} />
    </div>
  );
}

function AudioLibrary({ onError }) {
  const [audios, setAudios] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    myApi.selfTalkAudios().then(setAudios).catch(() => {});
  }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp3")) { onError?.("Só são aceites ficheiros mp3."); return; }
    setUploading(true);
    try {
      await myApi.selfTalkAudioUpload(file);
      setAudios(await myApi.selfTalkAudios());
    } catch (err) { onError?.(err.message); }
    finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const apagar = async (id) => {
    try {
      await myApi.selfTalkAudioDelete(id);
      setAudios((a) => a.filter((x) => x.id !== id));
    } catch (e) { onError?.(e.message); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="selftalk-audio-library">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Music size={20} style={{ color: GREEN }} />
            <h2 className="text-lg font-semibold" style={{ color: NAVY }}>Os teus áudios</h2>
          </div>
          <p className="text-sm text-[#6B7280] mt-1">
            Carrega os teus mp3 (grava o teu self-talk na tua voz) e ouve sempre que quiseres para te programares.
          </p>
        </div>
        <div>
          <input ref={inputRef} type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={onPick} data-testid="selftalk-audio-input" />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="selftalk-audio-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: GREEN }}
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading ? "A enviar…" : "Carregar mp3"}
          </button>
        </div>
      </div>

      {audios.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] py-6 text-center">Ainda não carregaste nenhum áudio.</p>
      ) : (
        <ul className="space-y-3">
          {audios.map((a) => (
            <li key={a.id} className="rounded-xl border border-[#E5E7EB] p-3" data-testid={`selftalk-audio-${a.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0F1829] truncate">{a.titulo}</p>
                  <p className="text-xs text-[#9CA3AF]">{fmtSize(a.tamanho)}</p>
                </div>
                <button onClick={() => apagar(a.id)} className="text-[#9CA3AF] hover:text-red-600 shrink-0" data-testid={`selftalk-audio-del-${a.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
              <audio controls preload="none" src={a.url} className="w-full" data-testid={`selftalk-audio-player-${a.id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
